import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import PropTypes from "prop-types";
import { fetchWorkcellDailyPlannedVsDone, fetchWorkcellDailyPlannedVsDoneDetails } from "../api";
import { formatHoursMinutes } from "../utils/time";
import {
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatLocalDateTime } from "../utils/localDateTime";
import { useTranslation } from "react-i18next";

const COLORS = ["#1976d2", "#ff9800", "#43a047"];

const formatDateTime = (value, t) => {
  if (!value) return t("workcell.dailyOverview.plannedVsDone.notAvailable");
  return formatLocalDateTime(value, true) || value;
};

const WorkcellDailyPlannedVsDoneChart = ({ date, workcellId, onClick, activeIndex, onHover, loading: loadingProp, onLoadingChange }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsHour, setDetailsHour] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const callParent = typeof onLoadingChange === "function";
    if (callParent) onLoadingChange(true);
    let mounted = true;
    fetchWorkcellDailyPlannedVsDone(date, workcellId)
      .then((result) => {
        if (!mounted) return;
        setData(result);
        setLoading(false);
        if (callParent) onLoadingChange(false);
      })
      .catch(() => {
        if (!mounted) return;
        setError(true);
        setLoading(false);
        if (callParent) onLoadingChange(false);
      });
    return () => { mounted = false; };
  }, [date, workcellId, onLoadingChange]);

  const openDetails = (hour) => {
    if (!hour) return;
    setDetailsOpen(true);
    setDetailsHour(hour);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetails(null);
    fetchWorkcellDailyPlannedVsDoneDetails(date, workcellId, hour)
      .then((result) => {
        setDetails(result);
        setDetailsLoading(false);
      })
      .catch(() => {
        setDetailsError(true);
        setDetailsLoading(false);
      });
  };

  const handleChartClick = (state) => {
    const hour = state?.activeLabel || state?.activePayload?.[0]?.payload?.hour;
    if (hour) {
      openDetails(hour);
      if (typeof onClick === "function") {
        onClick(hour, state);
      }
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsHour(null);
    setDetails(null);
    setDetailsError(null);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const plannedWorkDone = payload.find((p) => p.dataKey === "planned_work_done");
      const plannedNotDone = payload.find((p) => p.dataKey === "planned_not_done");
      const unplannedWorkDone = payload.find((p) => p.dataKey === "unplanned_work_done");
      return (
        <div className="custom-tooltip" style={{ background: "#fff", border: "1px solid #ccc", padding: 8 }}>
          <div><b>{t("daily.plannings.throughputChart.tooltip.hour")}:</b> {label}</div>
          {plannedWorkDone && (
            <div style={{ color: plannedWorkDone.color }}>
              {t("workcell.monthlyOverview.plannedVsDone.tooltip.plannedDone")}: {formatHoursMinutes(Number(plannedWorkDone.value))}
            </div>
          )}
          {plannedNotDone && (
            <div style={{ color: plannedNotDone.color }}>
              {t("workcell.monthlyOverview.plannedVsDone.tooltip.plannedNotDone")}: {formatHoursMinutes(Number(plannedNotDone.value))}
            </div>
          )}
          {unplannedWorkDone && (
            <div style={{ color: unplannedWorkDone.color }}>
              {t("workcell.monthlyOverview.plannedVsDone.tooltip.unplannedDone")}: {formatHoursMinutes(Number(unplannedWorkDone.value))}
            </div>
          )}
          <div style={{ marginTop: 8, color: "#666" }}>{t("workcell.dailyOverview.plannedVsDone.clickForDetails")}</div>
        </div>
      );
    }
    return null;
  };

  const isLoading = typeof loadingProp === "boolean" ? loadingProp : loading;
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <div>{t("common.failedToLoadData")}</div>;

  return (
    <>
      <div style={{ width: "100%", height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 16, right: 24, left: 8, bottom: 24 }}
            syncId="workcellCharts"
            onClick={handleChartClick}
          >
            <XAxis dataKey="hour" />
            <YAxis label={{ value: t("workcell.monthlyOverview.plannedVsDone.axisLabel"), angle: -90, position: "insideLeft" }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar
              dataKey="planned_work_done"
              stackId="a"
              fill={COLORS[0]}
              name={t("workcell.monthlyOverview.plannedVsDone.legend.plannedDone")}
            />
            <Bar
              dataKey="planned_not_done"
              stackId="a"
              fill={COLORS[1]}
              name={t("workcell.monthlyOverview.plannedVsDone.legend.plannedNotDone")}
            />
            <Bar
              dataKey="unplanned_work_done"
              stackId="a"
              fill={COLORS[2]}
              name={t("workcell.monthlyOverview.plannedVsDone.legend.unplannedDone")}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <Dialog open={detailsOpen} onClose={closeDetails} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pr: 6 }}>
          {t("workcell.dailyOverview.plannedVsDone.detailsTitle", { hour: detailsHour })}
          <IconButton
            aria-label={t("workcell.dailyOverview.plannedVsDone.close")}
            onClick={closeDetails}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={160}>
              <CircularProgress />
            </Box>
          )}
          {!detailsLoading && detailsError && <Typography color="error">{t("workcell.dailyOverview.plannedVsDone.detailsError")}</Typography>}
          {!detailsLoading && !detailsError && details && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("workcell.monthlyOverview.plannedVsDone.legend.plannedDone")}: {formatHoursMinutes(Number(details.planned_work_done || 0))} | {t("workcell.monthlyOverview.plannedVsDone.legend.unplannedDone")}: {formatHoursMinutes(Number(details.unplanned_work_done || 0))} | {t("workcell.dailyOverview.plannedVsDone.scheduled")}: {formatHoursMinutes(Number(details.scheduled_hours || 0))}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("workcell.dailyOverview.plannedVsDone.planningId")}</TableCell>
                      <TableCell>{t("workcell.dailyOverview.plannedVsDone.completedOn")}</TableCell>
                      <TableCell align="right">{t("workcell.monthlyOverview.plannedVsDone.legend.plannedDone")}</TableCell>
                      <TableCell align="right">{t("workcell.monthlyOverview.plannedVsDone.legend.unplannedDone")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.plannings?.length ? details.plannings.map((planning, index) => (
                      <TableRow key={`${planning.planning_id ?? "na"}-${planning.completed_at ?? "na"}-${index}`}>
                        <TableCell>{planning.planning_id ?? t("workcell.dailyOverview.plannedVsDone.notAvailable")}</TableCell>
                        <TableCell>{formatDateTime(planning.completed_at, t)}</TableCell>
                        <TableCell align="right">{formatHoursMinutes(Number(planning.planned_done_hours || 0), true)}</TableCell>
                        <TableCell align="right">{formatHoursMinutes(Number(planning.unplanned_done_hours || 0), true)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">{t("workcell.dailyOverview.plannedVsDone.noCompletedPlannings")}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

WorkcellDailyPlannedVsDoneChart.propTypes = {
  date: PropTypes.string.isRequired,
  workcellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClick: PropTypes.func,
  activeIndex: PropTypes.number,
  onHover: PropTypes.func,
  loading: PropTypes.bool,
};

export default WorkcellDailyPlannedVsDoneChart;
